import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { 
  Brain, Target, AlertTriangle, TrendingUp, Zap, CheckCircle,
  Clock, ArrowRight, Plus, X, Info
} from 'lucide-react';
import axios from 'axios';
import { getAuthHeaders } from '../utils/auth';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const IntelligenceDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [dailyBrief, setDailyBrief] = useState(null);
  const [profile, setProfile] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [recommendations, setRecommendations] = useState([]);
  const [stalledInitiatives, setStalledInitiatives] = useState([]);
  const [risks, setRisks] = useState([]);

  useEffect(() => {
    fetchAllIntelligence();
  }, []);

  const fetchAllIntelligence = async () => {
    try {
      setLoading(true);

      const [briefRes, profileRes, alertsRes, recsRes, stalledRes, risksRes] = await Promise.all([
        axios.get(`${API}/intelligence/daily-brief`, { headers: getAuthHeaders() }),
        axios.get(`${API}/intelligence/owner-profile`, { headers: getAuthHeaders() }),
        axios.get(`${API}/intelligence/alerts`, { headers: getAuthHeaders() }),
        axios.get(`${API}/intelligence/recommendations`, { headers: getAuthHeaders() }),
        axios.get(`${API}/intelligence/stalled-initiatives`, { headers: getAuthHeaders() }),
        axios.get(`${API}/intelligence/risks`, { headers: getAuthHeaders() })
      ]);

      setDailyBrief(briefRes.data);
      setProfile(profileRes.data);
      setAlerts(alertsRes.data.alerts || []);
      setRecommendations(recsRes.data.recommendations || []);
      setStalledInitiatives(stalledRes.data.stalled_initiatives || []);
      setRisks(risksRes.data.risks || []);
    } catch (error) {
      console.error('Failed to fetch intelligence:', error);
      toast.error('Failed to load intelligence data');
    } finally {
      setLoading(false);
    }
  };

  const handleAcknowledgeAlert = async (alertId) => {
    try {
      await axios.post(
        `${API}/intelligence/alert/${alertId}/acknowledge`,
        {},
        { headers: getAuthHeaders() }
      );
      
      setAlerts(alerts.filter(a => a._id !== alertId));
      toast.success('Alert acknowledged');
    } catch (error) {
      console.error('Failed to acknowledge alert:', error);
      toast.error('Failed to acknowledge alert');
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="inline-block h-12 w-12 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent"></div>
            <p className="text-muted-foreground mt-4">Loading Intelligence Layer...</p>
          </div>
        </div>
      </Layout>
    );
  }

  const intelligenceScore = profile?.intelligence_score || 0;

  return (
    <Layout>
      <div className="space-y-8" data-testid="intelligence-dashboard">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-5xl font-bold text-white mb-3">PERSONAL INTELLIGENCE</h1>
            <p className="text-muted-foreground text-lg">
              Lee learns you continuously • Proactive assistance • Zero friction
            </p>
          </div>
          <div className="glass-card p-6 border border-primary/30">
            <div className="text-center">
              <Brain className="w-8 h-8 text-primary mx-auto mb-2" />
              <p className="text-3xl font-bold text-white font-mono">{intelligenceScore}%</p>
              <p className="text-xs text-muted-foreground uppercase tracking-wider">Intelligence Depth</p>
            </div>
          </div>
        </div>

        {/* Critical Alerts */}
        {alerts.length > 0 && (
          <div className="glass-card p-8 border border-red-500/30 bg-red-500/5">
            <div className="flex items-center gap-3 mb-6">
              <AlertTriangle className="w-6 h-6 text-red-500" />
              <h2 className="text-2xl font-bold text-white uppercase tracking-tight">
                CRITICAL ATTENTION REQUIRED
              </h2>
              <span className="text-xs bg-red-500/20 text-red-500 px-3 py-1">
                {alerts.length} Alert{alerts.length > 1 ? 's' : ''}
              </span>
            </div>

            <div className="space-y-4">
              {alerts.map((alert, idx) => (
                <div
                  key={idx}
                  className="bg-background/50 p-6 border border-red-500/20 flex items-start justify-between"
                >
                  <div className="flex-1">
                    <p className="text-white font-semibold mb-2">{alert.title || alert.message}</p>
                    <p className="text-sm text-muted-foreground mb-3">{alert.message}</p>
                    {alert.action_required && (
                      <p className="text-sm text-primary">→ {alert.action_required}</p>
                    )}
                  </div>
                  <button
                    onClick={() => handleAcknowledgeAlert(alert._id)}
                    className="ml-4 text-muted-foreground hover:text-white transition-colors"
                    title="Acknowledge"
                  >
                    <CheckCircle className="w-5 h-5" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Stalled Initiatives */}
        {stalledInitiatives.length > 0 && (
          <div className="glass-card p-8 border border-yellow-500/30 bg-yellow-500/5">
            <div className="flex items-center gap-3 mb-6">
              <Clock className="w-6 h-6 text-yellow-500" />
              <h2 className="text-2xl font-bold text-white uppercase tracking-tight">
                STALLED INITIATIVES
              </h2>
              <span className="text-xs bg-yellow-500/20 text-yellow-500 px-3 py-1">
                {stalledInitiatives.length} Need Attention
              </span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {stalledInitiatives.map((initiative, idx) => (
                <div
                  key={idx}
                  className="bg-background/50 p-6 border border-yellow-500/20"
                >
                  <h3 className="text-lg font-bold text-white mb-2">{initiative.name}</h3>
                  <p className="text-sm text-muted-foreground mb-3">
                    {initiative.days_without_update} days without update
                  </p>
                  <p className="text-sm text-primary">→ {initiative.recommended_action}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Strategic Recommendations */}
        {recommendations.length > 0 && (
          <div className="glass-card p-8 border border-blue-500/30 bg-blue-500/5">
            <div className="flex items-center gap-3 mb-6">
              <Zap className="w-6 h-6 text-blue-500" />
              <h2 className="text-2xl font-bold text-white uppercase tracking-tight">
                STRATEGIC RECOMMENDATIONS
              </h2>
              <span className="text-xs bg-blue-500/20 text-blue-500 px-3 py-1">
                {recommendations.length} Suggestion{recommendations.length > 1 ? 's' : ''}
              </span>
            </div>

            <div className="space-y-4">
              {recommendations.map((rec, idx) => (
                <div
                  key={idx}
                  className="bg-background/50 p-6 border border-blue-500/20"
                >
                  <div className="flex items-start justify-between mb-3">
                    <span className={`text-xs uppercase px-2 py-1 ${
                      rec.priority === 'high' 
                        ? 'bg-red-500/20 text-red-500'
                        : rec.priority === 'medium'
                        ? 'bg-yellow-500/20 text-yellow-500'
                        : 'bg-green-500/20 text-green-500'
                    }`}>
                      {rec.priority} Priority
                    </span>
                    <span className="text-xs text-muted-foreground uppercase">{rec.category}</span>
                  </div>
                  <p className="text-white mb-3">{rec.recommendation}</p>
                  <div className="flex items-center gap-2 text-sm text-primary">
                    <ArrowRight className="w-4 h-4" />
                    <span>{rec.action}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Identified Risks */}
        {risks.length > 0 && (
          <div className="glass-card p-8 border border-white/10">
            <div className="flex items-center gap-3 mb-6">
              <AlertTriangle className="w-6 h-6 text-orange-500" />
              <h2 className="text-2xl font-bold text-white uppercase tracking-tight">
                IDENTIFIED RISKS
              </h2>
            </div>

            <div className="space-y-4">
              {risks.map((risk, idx) => (
                <div
                  key={idx}
                  className="bg-background/50 p-6 border border-orange-500/20"
                >
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-lg font-bold text-white">{risk.type.replace(/_/g, ' ').toUpperCase()}</h3>
                    <span className={`text-xs uppercase px-2 py-1 ${
                      risk.severity === 'high'
                        ? 'bg-red-500/20 text-red-500'
                        : risk.severity === 'medium'
                        ? 'bg-yellow-500/20 text-yellow-500'
                        : 'bg-green-500/20 text-green-500'
                    }`}>
                      {risk.severity} Severity
                    </span>
                  </div>
                  <p className="text-white mb-3">{risk.description}</p>
                  <p className="text-sm text-primary">Mitigation: {risk.mitigation}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Daily Brief */}
        {dailyBrief && dailyBrief.sections && dailyBrief.sections.length > 0 && (
          <div className="glass-card p-8 border border-white/10">
            <div className="flex items-center gap-3 mb-6">
              <Target className="w-6 h-6 text-primary" />
              <h2 className="text-2xl font-bold text-white uppercase tracking-tight">
                DAILY EXECUTIVE BRIEF
              </h2>
            </div>

            {dailyBrief.sections.map((section, idx) => (
              <div key={idx} className="mb-8 last:mb-0">
                <h3 className="text-lg font-bold text-white mb-4">{section.title}</h3>
                <div className="space-y-3">
                  {section.items.map((item, itemIdx) => (
                    <div
                      key={itemIdx}
                      className="bg-background/50 p-4 border border-white/5"
                    >
                      {typeof item === 'string' ? (
                        <p className="text-white">{item}</p>
                      ) : (
                        <div>
                          {item.metric && (
                            <div className="flex items-center justify-between">
                              <span className="text-muted-foreground">{item.metric}</span>
                              <span className="text-white font-mono">{item.value}</span>
                            </div>
                          )}
                          {item.name && (
                            <div>
                              <p className="text-white font-semibold mb-1">{item.name}</p>
                              {item.days_stalled && (
                                <p className="text-sm text-muted-foreground">{item.days_stalled} days stalled</p>
                              )}
                              {item.action && (
                                <p className="text-sm text-primary mt-2">→ {item.action}</p>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Intelligence Status */}
        <div className="glass-card p-8 border border-white/10">
          <div className="flex items-center gap-3 mb-6">
            <Info className="w-6 h-6 text-primary" />
            <h2 className="text-2xl font-bold text-white uppercase tracking-tight">
              ADAPTIVE INTELLIGENCE STATUS
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <p className="text-sm text-muted-foreground mb-2 uppercase tracking-wider">Learning Depth</p>
              <div className="flex items-center gap-3">
                <div className="flex-1 bg-background h-2 rounded-full overflow-hidden">
                  <div 
                    className="bg-primary h-full transition-all duration-500"
                    style={{ width: `${intelligenceScore}%` }}
                  />
                </div>
                <span className="text-white font-mono">{intelligenceScore}%</span>
              </div>
            </div>

            <div>
              <p className="text-sm text-muted-foreground mb-2 uppercase tracking-wider">Behavioral Patterns</p>
              <p className="text-white font-mono">
                {Object.keys(profile?.behavioral_patterns || {}).length} Learned
              </p>
            </div>

            <div>
              <p className="text-sm text-muted-foreground mb-2 uppercase tracking-wider">Active Learning</p>
              <p className="text-green-500 font-semibold">● Continuous</p>
            </div>
          </div>

          <div className="mt-6 pt-6 border-t border-white/10">
            <p className="text-sm text-muted-foreground mb-3">
              Lee adapts response style, identifies friction, and improves continuously based on your patterns.
            </p>
            <p className="text-xs text-primary uppercase tracking-wider">
              → Intelligence increases with every interaction
            </p>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default IntelligenceDashboard;
